import { HttpClient } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import {
  catchError,
  filter,
  forkJoin,
  from,
  map,
  mergeMap,
  Observable,
  of,
  pipe,
  tap,
  toArray,
} from 'rxjs';
import {
  IWHWorkerDTO,
  WHDataService,
  WHWorkerDOM,
} from '@workheld/workheld-shared-lib';
import { DataModelHelperService } from '../app-services-helper/data-model-helper.service';

export interface WorkerCacheEntry {
  name: string;
  avatarUrl: string;
  extId: string;
  workerDOM: WHWorkerDOM | null;
}

interface WorkerLookupState {
  cache: Record<string, WorkerCacheEntry>;
  loading: Record<string, boolean>;
}

const initialState: WorkerLookupState = {
  cache: {},
  loading: {},
};

function createEmptyCacheEntry(): WorkerCacheEntry {
  return { name: '', avatarUrl: '', extId: '', workerDOM: null };
}

function buildCacheEntry(
  dto: IWHWorkerDTO,
  dataModelHelper: DataModelHelperService,
): WorkerCacheEntry {
  const dtoAny = dto as unknown as Record<string, unknown>;
  const workerDOM = dataModelHelper.initWorkerDOM(dto);
  const lastname = (dtoAny['lastname'] as string) || '';
  const firstname = (dtoAny['firstname'] as string) || '';
  const name = lastname
    ? `${lastname} ${firstname}`
    : (dtoAny['name'] as string) || '';
  const avatarUrl = (dtoAny['avatarUrl'] as string) || '';
  const extId = (dtoAny['extId'] as string) || '';

  return { name, avatarUrl, extId, workerDOM };
}

/** Max ids per batch request; must match the backend limit on POST /workers/batch. */
export const WORKER_BATCH_SIZE = 200;

const WORKER_DETAIL_URL_REGEX = /^(.*\/workers)\/([0-9a-fA-F-]{36})\/?$/;

type WorkerFetchResult = { url: string; dto: IWHWorkerDTO | null };

/** Splits a worker detail URL into the collection base URL and the worker id. */
export function parseWorkerDetailUrl(
  url: string,
): { baseUrl: string; id: string } | null {
  const match = WORKER_DETAIL_URL_REGEX.exec(url);
  return match ? { baseUrl: match[1], id: match[2].toLowerCase() } : null;
}

function chunk<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

/** Max parallel per-URL requests when falling back from the batch endpoint. */
export const WORKER_FALLBACK_CONCURRENCY = 5;

/**
 * Fetches workers one URL at a time with bounded concurrency, so a failing
 * batch endpoint does not fan out into hundreds of simultaneous requests.
 */
function fetchWorkersIndividually(
  dataService: WHDataService,
  urls: string[],
): Observable<WorkerFetchResult[]> {
  return from(urls).pipe(
    mergeMap(
      (url) =>
        dataService.getByUrl(url).pipe(
          map((dto) => ({ url, dto: (dto ?? null) as IWHWorkerDTO | null })),
          catchError(() => of({ url, dto: null as IWHWorkerDTO | null })),
        ),
      WORKER_FALLBACK_CONCURRENCY,
    ),
    toArray(),
  );
}

/**
 * Loads several workers with as few requests as possible: worker detail URLs
 * are grouped by base URL and fetched via POST /workers/batch (ids in the
 * request body) in batches. URLs that cannot be parsed, or a batch that fails,
 * fall back to per-URL requests.
 */
function fetchWorkers(
  dataService: WHDataService,
  httpClient: HttpClient,
  urls: string[],
): Observable<WorkerFetchResult[]> {
  const groups = new Map<string, { url: string; id: string }[]>();
  const unparsable: string[] = [];

  for (const url of urls) {
    const parsed = parseWorkerDetailUrl(url);
    if (!parsed) {
      unparsable.push(url);
      continue;
    }
    const group = groups.get(parsed.baseUrl) ?? [];
    group.push({ url, id: parsed.id });
    groups.set(parsed.baseUrl, group);
  }

  const requests: Observable<WorkerFetchResult[]>[] = [];

  groups.forEach((entries, baseUrl) => {
    for (const batch of chunk(entries, WORKER_BATCH_SIZE)) {
      requests.push(
        httpClient
          .post<IWHWorkerDTO[]>(
            `${baseUrl}/batch`,
            batch.map((entry) => entry.id),
          )
          .pipe(
            map((dtos) => {
              const byId = new Map<string, IWHWorkerDTO>();
              for (const dto of dtos ?? []) {
                const id = (dto as unknown as Record<string, unknown>)?.['id'];
                if (typeof id === 'string') {
                  byId.set(id.toLowerCase(), dto);
                }
              }
              return batch.map(({ url, id }) => ({
                url,
                dto: byId.get(id) ?? null,
              }));
            }),
            catchError(() =>
              fetchWorkersIndividually(
                dataService,
                batch.map((entry) => entry.url),
              ),
            ),
          ),
      );
    }
  });

  if (unparsable.length > 0) {
    requests.push(fetchWorkersIndividually(dataService, unparsable));
  }

  if (requests.length === 0) {
    return of([]);
  }

  return forkJoin(requests).pipe(map((results) => results.flat()));
}

export const WorkerLookupStore = signalStore(
  { providedIn: 'root' },

  withState(initialState),

  withComputed(({ cache }) => ({
    workerByUrl: computed(() => cache()),
  })),

  withMethods(
    (
      store,
      dataService = inject(WHDataService),
      httpClient = inject(HttpClient),
      dataModelHelper = inject(DataModelHelperService),
    ) => ({
      getWorker(url: string): WorkerCacheEntry | undefined {
        return store.cache()[url];
      },

      loadWorker: rxMethod<string>(
        pipe(
          filter(
            (url) => !!url && !store.cache()[url] && !store.loading()[url],
          ),
          tap((url) =>
            patchState(store, (state) => ({
              loading: { ...state.loading, [url]: true },
            })),
          ),
          mergeMap((url) =>
            dataService.getByUrl(url).pipe(
              map((dto) => ({ url, dto })),
              catchError(() => of({ url, dto: null as IWHWorkerDTO | null })),
            ),
          ),
          tapResponse({
            next: ({ url, dto }) => {
              const entry = dto
                ? buildCacheEntry(dto, dataModelHelper)
                : createEmptyCacheEntry();
              patchState(store, (state) => ({
                cache: { ...state.cache, [url]: entry },
                loading: { ...state.loading, [url]: false },
              }));
            },
            error: () => {},
          }),
        ),
      ),

      loadWorkers: rxMethod<string[]>(
        pipe(
          map((urls) => [
            ...new Set(
              urls.filter(
                (url) => url && !store.cache()[url] && !store.loading()[url],
              ),
            ),
          ]),
          filter((urls) => urls.length > 0),
          tap((urls) => {
            const loadingEntries = Object.fromEntries(
              urls.map((url) => [url, true]),
            );
            patchState(store, (state) => ({
              loading: { ...state.loading, ...loadingEntries },
            }));
          }),
          mergeMap((urls) => fetchWorkers(dataService, httpClient, urls)),
          tapResponse({
            next: (results: WorkerFetchResult[]) => {
              const newCache: Record<string, WorkerCacheEntry> = {};
              const newLoading: Record<string, boolean> = {};
              for (const { url, dto } of results) {
                newCache[url] = dto
                  ? buildCacheEntry(dto, dataModelHelper)
                  : createEmptyCacheEntry();
                newLoading[url] = false;
              }
              patchState(store, (state) => ({
                cache: { ...state.cache, ...newCache },
                loading: { ...state.loading, ...newLoading },
              }));
            },
            error: () => {},
          }),
        ),
      ),
    }),
  ),
);
